import { useQuery } from '@tanstack/react-query';
import { getPipelineRunSteps, type AdminPipelinesApiError } from '@/api/adminPipelines';
import { formatMs } from '@/features/admin/formatMs';
import styles from '@/features/admin/PipelineRunsPage.module.css';

/*
 * Expanded row of the report-generation log: the per-step timing breakdown of
 * one run. Bars are proportional to the SLOWEST step so the bottleneck is
 * readable at a glance. Steps overlap when the pipeline runs them concurrently,
 * so the sum of step durations is cumulative work, not the run's wall-clock.
 */
/** Steps of a run in flight are polled this often; a finished run is static. */
const LIVE_POLL_MS = 3_000;

export function RunStepsPanel({ baseUrl, runId }: { baseUrl: string; runId: string }) {
  /*
   * The pipeline now writes each step row as the step finishes, so a run in
   * flight has a growing step list — poll while it is `running` and stop the
   * moment it settles. A finished run's steps never change, hence the long
   * staleTime for those.
   */
  const stepsQuery = useQuery({
    queryKey: ['admin', 'pipeline-runs', baseUrl, runId, 'steps'],
    queryFn: () => getPipelineRunSteps(baseUrl, runId),
    refetchInterval: (query) => (query.state.data?.run.status === 'running' ? LIVE_POLL_MS : false),
    staleTime: (query) => (query.state.data?.run.status === 'running' ? 0 : 60_000),
  });

  if (stepsQuery.isLoading) return <div className={styles.stepsState}>Loading steps…</div>;
  if (stepsQuery.isError) {
    return <div className={styles.stepsState}>Could not load steps: {(stepsQuery.error as AdminPipelinesApiError)?.message ?? 'unknown error'}</div>;
  }

  const { run, steps } = stepsQuery.data!;
  const isRunning = run.status === 'running';

  // A run in flight legitimately has no steps yet — say "waiting", not "none
  // recorded", which reads like the run failed to record them.
  if (steps.length === 0) {
    return (
      <div className={styles.stepsState}>{isRunning ? 'Running — waiting for the first step to finish…' : 'No step timings were recorded for this run.'}</div>
    );
  }

  const max = Math.max(...steps.map((s) => s.duration_ms), 1);
  const totalWork = steps.reduce((acc, s) => acc + s.duration_ms, 0);

  return (
    <div className={styles.steps}>
      {steps.map((s) => (
        <div key={s.seq} className={styles.stepRow}>
          <div className={styles.stepName} title={s.error ?? undefined}>
            {s.step}
            {s.status === 'error' && <span className={styles.stepError}> · failed</span>}
          </div>
          <div className={styles.stepTrack}>
            <div
              className={`${styles.stepFill}${s.status === 'error' ? ` ${styles.stepFillError}` : ''}`}
              style={{ width: `${Math.max(1, (s.duration_ms / max) * 100)}%` }}
            />
          </div>
          <div className={styles.stepDuration}>{formatMs(s.duration_ms)}</div>
        </div>
      ))}
      <div className={styles.stepsFoot}>
        {isRunning ? (
          <>
            <span className={styles.stepsLive}>● live</span> {steps.length} step{steps.length === 1 ? '' : 's'} done · {formatMs(totalWork)} of work so far —
            updating every {LIVE_POLL_MS / 1000}s.
          </>
        ) : (
          <>
            Cumulative step work {formatMs(totalWork)} · run wall-clock {formatMs(run.duration_ms)} — steps overlap when the pipeline computes metrics
            concurrently.
          </>
        )}
      </div>
    </div>
  );
}
